import { ApiResponse } from '@/domain'
import { Collector } from '@/domain/collectors/collector'
import { SaleByOffering, SaleBySeller, SalesSummary, SalesSummaryByOffering } from '@/domain/sellers/sellers'
import { useToast } from '@/hooks/use-toast'
import { sellersApi } from '@/rest/modules/sellers-api'
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'

export const sellerKeys = {
  all: ['sellers'] as const,
  lists: () => [...sellerKeys.all, 'list'] as const,
  sellers: (filters: { status?: string; name?: string; doc_number?: string; code_lte?: number }) => [...sellerKeys.lists(), { filters }] as const,
  details: () => [...sellerKeys.all, 'detail'] as const,
  detail: (id: string) => [...sellerKeys.details(), id] as const,
  billingPeriods: (sellerId: string, fromDate: string, toDate?: string, page?: number, limit?: number, status?: string, serviceType?: string) =>
    [...sellerKeys.all, 'billing-periods', { sellerId, fromDate, toDate, page, limit, status, serviceType }] as const,
  summaryBySeller: (fromDate: Date, toDate?: Date, sellerId?: string, serviceType?: string) =>
    [...sellerKeys.all, 'summary', { fromDate, toDate, sellerId, serviceType }] as const,
  summaryByOfferings: (fromDate: Date, toDate?: Date, serviceType?: string, offeringId?: string) =>
    [...sellerKeys.all, 'offerings', { fromDate, toDate, serviceType, offeringId }] as const,
  salesBySeller: (sellerId: string, fromDate: Date, toDate?: Date) =>
    [...sellerKeys.all, 'contracts', { sellerId, fromDate, toDate }] as const,
  salesByOffering: (offeringId: string, fromDate: Date, toDate?: Date) =>
    [...sellerKeys.all, 'offering-contracts', { offeringId, fromDate, toDate }] as const,
}

export const useSellers = (
  filters?: { status?: string; name?: string; }
): UseQueryResult<Collector[], unknown> => {
  return useQuery({
    queryKey: sellerKeys.sellers(filters || {}),
    queryFn: () => sellersApi.getAllSellers(filters),
    staleTime: 0,
  })
}

export const useSeller = (id: string): UseQueryResult<Collector, unknown> => {
  return useQuery({
    queryKey: sellerKeys.detail(id),
    queryFn: () => sellersApi.getSeller(id),
    enabled: !!id,
  })
}

export const useCreateSeller = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: Partial<Collector>) => sellersApi.createSeller(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerKeys.all })
      toast({
        title: 'Éxito',
        description: 'Vendedor creado correctamente',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear vendedor',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateSeller = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Collector> }) => sellersApi.updateSeller(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerKeys.all })
      toast({
        title: 'Éxito',
        description: 'Vendedor actualizado correctamente',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar vendedor',
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteSeller = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => sellersApi.deleteSeller(id).then(() => id),
    onSuccess: (deletedId) => {
      queryClient.removeQueries({ queryKey: sellerKeys.detail(deletedId) })
      queryClient.invalidateQueries({ queryKey: sellerKeys.all })
      toast({
        title: 'Éxito',
        description: 'Vendedor eliminado correctamente',
        variant: 'default',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar vendedor',
        variant: 'destructive',
      })
    },
  })
}

export const useSalesSummaryBySeller = (
  fromDate: Date,
  toDate?: Date,
  sellerId?: string,
  serviceType?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<SalesSummary | null, unknown>({
    queryKey: sellerKeys.summaryBySeller(fromDate, toDate, sellerId, serviceType),
    queryFn: () => sellersApi.getSalesSummaryBySeller(fromDate, toDate, sellerId, serviceType),
    enabled: !!fromDate && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 2,
  })
}

export const useSalesSummaryByOffering = (
  fromDate: Date,
  toDate?: Date,
  serviceType?: string,
  offeringId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<SalesSummaryByOffering[], unknown>({
    queryKey: sellerKeys.summaryByOfferings(fromDate, toDate, serviceType, offeringId),
    queryFn: () => sellersApi.getSalesSummaryByOffering(fromDate, toDate, serviceType, offeringId),
    enabled: !!fromDate && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 2,
  })
}

export const useSalesBySeller = (
  sellerId: string,
  fromDate: Date,
  toDate?: Date,
  options?: { enabled?: boolean }
): UseQueryResult<SaleBySeller[], unknown> => {
  return useQuery({
    queryKey: sellerKeys.salesBySeller(sellerId, fromDate, toDate),
    queryFn: () => sellersApi.getSalesBySeller(sellerId, fromDate, toDate),
    enabled: !!sellerId && !!fromDate && (options?.enabled ?? false),
    staleTime: 0,
  })
}

export const useSalesByOffering = (
  offeringId: string,
  fromDate: Date,
  toDate?: Date,
  options?: { enabled?: boolean }
): UseQueryResult<SaleByOffering[], unknown> => {
  return useQuery({
    queryKey: sellerKeys.salesByOffering(offeringId, fromDate, toDate),
    queryFn: () => sellersApi.getSalesByOffering(offeringId, fromDate, toDate),
    enabled: !!offeringId && !!fromDate && (options?.enabled ?? false),
    staleTime: 0,
  })
}
